import React, { useMemo, useState } from 'react';
import styled from 'styled-components';

import {
  calculateQuote,
  emptyMaterialLine,
  LABOUR_MODES,
  TAX_MODES,
  parseDecimal,
  roundMoney,
} from '../../Domain/quote';
import { useI18n } from '../../i18n/I18nProvider';

const TAX_OPTIONS = [
  { value: '', fr: 'Choisir le régime de TVA', en: 'Choose VAT treatment' },
  { value: TAX_MODES.EXEMPT, fr: 'TVA non applicable — art. 293 B du CGI', en: 'VAT not applicable — French CGI art. 293 B' },
  { value: TAX_MODES.VAT_5_5, fr: 'TVA 5,5 %', en: 'VAT 5.5%' },
  { value: TAX_MODES.VAT_10, fr: 'TVA 10 %', en: 'VAT 10%' },
  { value: TAX_MODES.VAT_20, fr: 'TVA 20 %', en: 'VAT 20%' },
];

const normalizeValue = (value = {}) => ({
  materials: Array.isArray(value.materials) && value.materials.length
    ? value.materials.map((item = {}) => ({
        material_name: String(item.material_name ?? ''),
        material_quantity: String(item.material_quantity ?? ''),
        material_price: String(item.material_price ?? ''),
      }))
    : [emptyMaterialLine()],
  labourMode: value.labourMode === LABOUR_MODES.FIXED ? LABOUR_MODES.FIXED : LABOUR_MODES.HOURS,
  labourHours: String(value.labourHours ?? ''),
  hourlyRate: String(value.hourlyRate ?? ''),
  wage: String(value.wage ?? ''),
  travelCost: String(value.travelCost ?? '0'),
  taxMode: String(value.taxMode ?? ''),
});

/**
 * v13-controlled-local
 *
 * One state owns every editable price field. No refs, no DOM polling, no
 * duplicated draft/liveQuote state and no prop mirroring while the modal is
 * being edited.
 *
 * ContractModal already remounts this editor with a new key whenever a quote
 * modal opens, so the initial prop is only used once for the editing session.
 */
const QuoteCostEditor = ({ value, onChange, showValidation = false }) => {
  const { language, formatCurrency, pick } = useI18n();
  const [draft, setDraft] = useState(() => normalizeValue(value));

  const quote = useMemo(() => calculateQuote(draft), [draft]);

  const publish = (nextDraft) => {
    const normalized = normalizeValue(nextDraft);
    setDraft(normalized);
    onChange?.(normalized);
  };

  const updateScalar = (field, nextValue) => {
    publish({ ...draft, [field]: nextValue });
  };

  const updateMaterial = (index, field, nextValue) => {
    const materials = draft.materials.map((material, materialIndex) => (
      materialIndex === index
        ? { ...material, [field]: nextValue }
        : material
    ));
    publish({ ...draft, materials });
  };

  const addMaterial = () => {
    publish({ ...draft, materials: [...draft.materials, emptyMaterialLine()] });
  };

  const removeMaterial = (index) => {
    const materials = draft.materials.filter((_, materialIndex) => materialIndex !== index);
    publish({
      ...draft,
      materials: materials.length ? materials : [emptyMaterialLine()],
    });
  };

  const lineTotal = (material) => {
    const quantity = parseDecimal(material.material_quantity);
    const price = parseDecimal(material.material_price);
    if (!Number.isFinite(quantity) || quantity <= 0) return 0;
    if (!Number.isFinite(price) || price < 0) return 0;
    return roundMoney(quantity * price);
  };

  const inputProps = (handler) => ({
    onChange: handler,
    onInput: handler,
    autoComplete: 'off',
  });

  return (
    <Wrapper data-lpb-quote-calculator="v13-controlled-local">
      <MaterialSection>
        <SectionHeader>
          <div>
            <h3>{pick('Matériaux et fournitures', 'Materials and supplies')}</h3>
            <p>{pick(
              'Saisissez la quantité et le prix unitaire HT. Le total de chaque ligne est recalculé immédiatement.',
              'Enter quantity and unit price excluding tax. Each line total is recalculated immediately.',
            )}</p>
          </div>
          <AddButton type="button" onClick={addMaterial}>
            + {pick('Ajouter une ligne', 'Add line')}
          </AddButton>
        </SectionHeader>

        <MaterialList>
          {draft.materials.map((material, index) => (
            <MaterialRow key={`material-${index}`} data-lpb-material-row>
              <MaterialField $name>
                <label htmlFor={`quote-material-name-${index}`}>
                  {pick('Désignation', 'Description')}
                </label>
                <input
                  id={`quote-material-name-${index}`}
                  value={material.material_name}
                  {...inputProps((event) => updateMaterial(index, 'material_name', event.currentTarget.value))}
                  placeholder={pick('Ex. peinture murale', 'E.g. wall paint')}
                  maxLength={160}
                />
              </MaterialField>

              <MaterialField>
                <label htmlFor={`quote-material-quantity-${index}`}>
                  {pick('Quantité', 'Quantity')}
                </label>
                <input
                  id={`quote-material-quantity-${index}`}
                  type="text"
                  inputMode="decimal"
                  value={material.material_quantity}
                  {...inputProps((event) => updateMaterial(index, 'material_quantity', event.currentTarget.value))}
                  placeholder="1"
                />
              </MaterialField>

              <MaterialField>
                <label htmlFor={`quote-material-price-${index}`}>
                  {pick('Prix unitaire HT', 'Unit price excl. tax')}
                </label>
                <input
                  id={`quote-material-price-${index}`}
                  type="text"
                  inputMode="decimal"
                  value={material.material_price}
                  {...inputProps((event) => updateMaterial(index, 'material_price', event.currentTarget.value))}
                  placeholder="0,00"
                />
              </MaterialField>

              <LineTotal>
                <span>{pick('Total ligne', 'Line total')}</span>
                <strong>{formatCurrency(lineTotal(material))}</strong>
              </LineTotal>

              <RemoveButton
                type="button"
                aria-label={pick(`Supprimer la ligne ${index + 1}`, `Remove line ${index + 1}`)}
                onClick={() => removeMaterial(index)}
              >
                ×
              </RemoveButton>
            </MaterialRow>
          ))}
        </MaterialList>
      </MaterialSection>

      <LabourSection>
        <SectionHeader>
          <div>
            <h3>{pick('Main-d’œuvre', 'Labour')}</h3>
            <p>{pick(
              'Utilisez les heures et votre taux horaire, ou choisissez un forfait.',
              'Use hours and your hourly rate, or choose a fixed labour amount.',
            )}</p>
          </div>

          <ModeSwitch role="group" aria-label={pick('Mode de calcul de la main-d’œuvre', 'Labour calculation mode')}>
            <ModeButton
              type="button"
              $active={draft.labourMode === LABOUR_MODES.HOURS}
              onClick={() => updateScalar('labourMode', LABOUR_MODES.HOURS)}
            >
              {pick('Heures × taux', 'Hours × rate')}
            </ModeButton>
            <ModeButton
              type="button"
              $active={draft.labourMode === LABOUR_MODES.FIXED}
              onClick={() => updateScalar('labourMode', LABOUR_MODES.FIXED)}
            >
              {pick('Forfait', 'Fixed amount')}
            </ModeButton>
          </ModeSwitch>
        </SectionHeader>

        {draft.labourMode === LABOUR_MODES.HOURS ? (
          <LabourGrid>
            <Field>
              <label htmlFor="quote-labour-hours">
                {pick('Heures prévues', 'Planned hours')}
              </label>
              <input
                id="quote-labour-hours"
                type="text"
                inputMode="decimal"
                value={draft.labourHours}
                {...inputProps((event) => updateScalar('labourHours', event.currentTarget.value))}
                placeholder="Ex. 8"
              />
            </Field>

            <Field>
              <label htmlFor="quote-hourly-rate">
                {pick('Taux horaire HT', 'Hourly rate excl. tax')}
              </label>
              <input
                id="quote-hourly-rate"
                type="text"
                inputMode="decimal"
                value={draft.hourlyRate}
                {...inputProps((event) => updateScalar('hourlyRate', event.currentTarget.value))}
                placeholder="Ex. 45,00"
              />
            </Field>

            <ComputedLabour>
              <span>{pick('Main-d’œuvre calculée', 'Calculated labour')}</span>
              <strong>{formatCurrency(quote.wage)}</strong>
              <small>
                {quote.labourHours > 0 && quote.hourlyRate > 0
                  ? `${quote.labourHours} h × ${formatCurrency(quote.hourlyRate)}`
                  : quote.labourHours > 0
                    ? pick('Ajoutez le taux horaire HT pour calculer la main-d’œuvre.', 'Enter the hourly rate to calculate labour.')
                    : pick('Renseignez les heures et le taux.', 'Enter hours and rate.')}
              </small>
            </ComputedLabour>
          </LabourGrid>
        ) : (
          <LabourGrid $fixed>
            <Field>
              <label htmlFor="quote-wage">
                {pick('Forfait main-d’œuvre HT', 'Fixed labour excl. tax')}
              </label>
              <input
                id="quote-wage"
                type="text"
                inputMode="decimal"
                value={draft.wage}
                {...inputProps((event) => updateScalar('wage', event.currentTarget.value))}
                placeholder="0,00"
              />
            </Field>

            <ComputedLabour>
              <span>{pick('Main-d’œuvre retenue', 'Labour total')}</span>
              <strong>{formatCurrency(quote.wage)}</strong>
            </ComputedLabour>
          </LabourGrid>
        )}
      </LabourSection>

      <Costs>
        <Field>
          <label htmlFor="quote-travel">
            {pick('Déplacement HT', 'Travel excl. tax')}
          </label>
          <input
            id="quote-travel"
            type="text"
            inputMode="decimal"
            value={draft.travelCost}
            {...inputProps((event) => updateScalar('travelCost', event.currentTarget.value))}
            placeholder="0,00"
          />
        </Field>

        <Field>
          <label htmlFor="quote-tax">
            {pick('Régime de TVA', 'VAT treatment')}
          </label>
          <select
            id="quote-tax"
            value={draft.taxMode}
            onChange={(event) => updateScalar('taxMode', event.currentTarget.value)}
          >
            {TAX_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {language === 'en' ? option.en : option.fr}
              </option>
            ))}
          </select>
        </Field>
      </Costs>

      <Summary aria-live="polite">
        <Cost>
          <span>{pick('Matériaux', 'Materials')}</span>
          <strong>{formatCurrency(quote.materialCost)}</strong>
        </Cost>
        <Cost>
          <span>{pick('Main-d’œuvre', 'Labour')}</span>
          <strong>{formatCurrency(quote.wage)}</strong>
        </Cost>
        <Cost>
          <span>{pick('Déplacement', 'Travel')}</span>
          <strong>{formatCurrency(quote.travelCost)}</strong>
        </Cost>
        <Cost>
          <span>{pick('Sous-total HT', 'Subtotal excl. tax')}</span>
          <strong>{formatCurrency(quote.subtotalHt)}</strong>
        </Cost>
        <Cost>
          <span>{draft.taxMode === TAX_MODES.EXEMPT ? 'TVA' : `TVA ${quote.vatRate || 0} %`}</span>
          <strong>{formatCurrency(quote.vatAmount)}</strong>
        </Cost>
        <Total>
          <span>{pick('Total TTC du devis', 'Quote total incl. tax')}</span>
          <strong>{formatCurrency(quote.totalTtc)}</strong>
        </Total>
      </Summary>

      {showValidation && quote.errors.length > 0 && (
        <Validation role="alert">
          {quote.errors.slice(0, 5).map((message) => <span key={message}>• {message}</span>)}
        </Validation>
      )}
    </Wrapper>
  );
};

export const quoteFromEditorValue = (value) => calculateQuote(normalizeValue(value));

const Wrapper = styled.div`display:grid;gap:1rem;min-width:0;position:relative;`;
const MaterialSection = styled.section`padding:1rem;border:1px solid #e5e1da;border-radius:1rem;background:#fff;`;
const LabourSection = styled(MaterialSection)`background:#fbf9f5;`;
const SectionHeader = styled.div`
  display:flex;align-items:flex-start;justify-content:space-between;gap:1rem;margin-bottom:.85rem;min-width:0;
  >div{min-width:0;}h3{margin:0;color:#111;}p{margin:.25rem 0 0;color:#6c7983;font-size:.8rem;line-height:1.45;}
  @media(max-width:620px){display:grid;grid-template-columns:1fr;}
`;
const AddButton = styled.button`border:1px solid #c0a375;border-radius:999px;background:#f7f1e7;color:#111;padding:.58rem .82rem;font:inherit;font-weight:800;cursor:pointer;white-space:nowrap;@media(max-width:520px){width:100%;}`;
const MaterialList = styled.div`display:grid;gap:.72rem;`;
const MaterialRow = styled.div`
  display:grid;grid-template-columns:minmax(0,2fr) minmax(5rem,.65fr) minmax(7rem,.8fr) minmax(7rem,.8fr) auto;align-items:end;gap:.55rem;
  @media(max-width:760px){grid-template-columns:minmax(0,1fr) minmax(0,1fr) auto;&>:first-child{grid-column:1 / -1;}&>:nth-child(4){grid-column:1 / 3;}}
  @media(max-width:430px){grid-template-columns:1fr;&>*{grid-column:auto!important;}}
`;
const MaterialField = styled.div`display:grid;gap:.3rem;min-width:0;label{color:#726b61;font-size:.7rem;font-weight:850;text-transform:uppercase;}input{width:100%;min-width:0;box-sizing:border-box;padding:.7rem;border:1px solid #ddd3c4;border-radius:.7rem;font:inherit;font-size:1rem;}`;
const LineTotal = styled.div`display:grid;gap:.18rem;min-height:2.72rem;align-content:center;padding:.5rem .65rem;border-radius:.7rem;background:#f6f3ee;span{color:#726b61;font-size:.62rem;text-transform:uppercase;}strong{font-size:.82rem;white-space:nowrap;}`;
const RemoveButton = styled.button`width:2.65rem;height:2.65rem;border:0;border-radius:50%;background:#fff0ef;color:#9a2f29;font-size:1.3rem;cursor:pointer;@media(max-width:430px){justify-self:end;}`;
const ModeSwitch = styled.div`display:inline-grid;grid-template-columns:repeat(2,auto);gap:.25rem;padding:.2rem;border-radius:999px;background:#eee9e1;@media(max-width:440px){grid-template-columns:1fr 1fr;width:100%;}`;
const ModeButton = styled.button`min-height:2.25rem;padding:.4rem .7rem;border:0;border-radius:999px;background:${({ $active }) => ($active ? '#111' : 'transparent')};color:${({ $active }) => ($active ? '#fff' : '#5f5850')};font:inherit;font-size:.72rem;font-weight:850;cursor:pointer;`;
const LabourGrid = styled.div`display:grid;grid-template-columns:${({ $fixed }) => ($fixed ? 'minmax(0,1fr) minmax(12rem,.7fr)' : 'repeat(2,minmax(0,1fr)) minmax(12rem,.8fr)')};gap:.8rem;align-items:end;@media(max-width:680px){grid-template-columns:1fr;}`;
const Costs = styled.div`display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:.8rem;@media(max-width:680px){grid-template-columns:1fr;}`;
const Field = styled.div`display:grid;gap:.38rem;label{color:#222;font-weight:800;}input,select{width:100%;min-width:0;box-sizing:border-box;padding:.78rem;border:1px solid #ddd3c4;border-radius:.75rem;color:#111;background:#fff;font:inherit;font-size:1rem;}`;
const ComputedLabour = styled.div`display:grid;gap:.12rem;min-height:3.1rem;align-content:center;padding:.72rem .85rem;border-radius:.8rem;background:#151719;color:#fff;span{color:#c8cdd0;font-size:.64rem;text-transform:uppercase;}strong{color:#f1c978;font-size:1.05rem;}small{color:#c8cdd0;font-size:.64rem;}`;
const Summary = styled.div`display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:.65rem;@media(max-width:720px){grid-template-columns:repeat(2,minmax(0,1fr));}@media(max-width:430px){grid-template-columns:1fr;}`;
const Cost = styled.div`display:grid;gap:.2rem;padding:.8rem 1rem;border-radius:.8rem;background:#faf8f4;span{color:#726b61;font-size:.68rem;text-transform:uppercase;}strong{overflow-wrap:anywhere;}`;
const Total = styled(Cost)`background:#142f49;color:#fff;span{color:#dce7ef;}strong{color:#f2c567;font-size:1.15rem;}`;
const Validation = styled.div`display:grid;gap:.25rem;padding:.8rem .9rem;border:1px solid #efc0bc;border-radius:.8rem;background:#fff2f1;color:#932f29;font-size:.78rem;line-height:1.45;`;

export default QuoteCostEditor;
